import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onboardingAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { 
  Smile, Meh, Frown, Cloud, Zap, Heart, Brain, Users, 
  Sun, Coffee, Sparkles, ChevronRight, ChevronLeft, Check 
} from 'lucide-react';

const MOODS = [
  { id: 'great', icon: Smile, label: 'Great', color: 'text-emerald-500' },
  { id: 'good', icon: Sun, label: 'Good', color: 'text-sage-400' },
  { id: 'okay', icon: Meh, label: 'Okay', color: 'text-sand-500' },
  { id: 'low', icon: Cloud, label: 'Low', color: 'text-slate-400' },
  { id: 'difficult', icon: Frown, label: 'Difficult', color: 'text-terracotta-400' },
];

const THERAPY_GOALS = [
  { id: 'stress', icon: Zap, label: 'Managing Stress' },
  { id: 'anxiety', icon: Cloud, label: 'Reducing Anxiety' },
  { id: 'loneliness', icon: Users, label: 'Feeling Less Lonely' },
  { id: 'burnout', icon: Coffee, label: 'Recovering from Burnout' },
  { id: 'overthinking', icon: Brain, label: 'Stopping Overthinking' },
  { id: 'support', icon: Heart, label: 'General Emotional Support' },
];

const TONE_PREFERENCES = [
  { id: 'calm', icon: '🌿', label: 'Calm & Gentle', description: 'Soft, soothing responses' },
  { id: 'friendly', icon: '☀️', label: 'Warm & Friendly', description: 'Conversational, upbeat' },
  { id: 'professional', icon: '📋', label: 'Professional', description: 'Direct, structured guidance' },
];

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState({
    mood: '',
    therapy_goals: [],
    tone_preference: '',
    disclaimer_accepted: false,
  });

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  const handleNext = () => setStep(s => Math.min(s + 1, totalSteps));
  const handleBack = () => setStep(s => Math.max(s - 1, 1));

  const handleGoalToggle = (goalId) => {
    setData(prev => ({
      ...prev,
      therapy_goals: prev.therapy_goals.includes(goalId)
        ? prev.therapy_goals.filter(g => g !== goalId)
        : [...prev.therapy_goals, goalId]
    }));
  };

  const handleComplete = async () => {
    if (!data.disclaimer_accepted) return;
    setIsLoading(true);
    try {
      await onboardingAPI.save(data);
      navigate('/chat');
    } catch (error) {
      console.error('Onboarding error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return data.mood !== '';
      case 2: return data.therapy_goals.length > 0;
      case 3: return data.tone_preference !== '';
      case 4: return data.disclaimer_accepted;
      default: return false;
    }
  };

  return (
    <div className="min-h-screen bg-background grain flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-xl">
        {/* Progress */}
        <div className="mb-6 sm:mb-8 space-y-2">
          <div className="flex justify-between text-xs sm:text-sm text-muted-foreground">
            <span>Step {step} of {totalSteps}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <Card className="shadow-floating border-0 animate-fade-in">
          {/* Step 1: Mood */}
          {step === 1 && (
            <>
              <CardHeader className="text-center px-4 sm:px-6">
                <CardTitle className="text-xl sm:text-2xl font-heading">How are you feeling today?</CardTitle>
                <CardDescription className="text-sm">Let's start by checking in with yourself</CardDescription>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                <div className="grid grid-cols-5 gap-2 sm:gap-3">
                  {MOODS.map((mood) => (
                    <button
                      key={mood.id}
                      onClick={() => setData({ ...data, mood: mood.id })}
                      className={`flex flex-col items-center gap-1 sm:gap-2 p-2 sm:p-4 rounded-xl sm:rounded-2xl transition-all duration-300 ${
                        data.mood === mood.id
                          ? 'bg-primary/10 ring-2 ring-primary scale-105'
                          : 'bg-muted/50 hover:bg-muted'
                      }`}
                      data-testid={`mood-${mood.id}`}
                    >
                      <mood.icon className={`w-6 h-6 sm:w-8 sm:h-8 ${mood.color}`} />
                      <span className="text-xs font-medium">{mood.label}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </>
          )}

          {/* Step 2: Goals */}
          {step === 2 && (
            <>
              <CardHeader className="text-center px-4 sm:px-6">
                <CardTitle className="text-xl sm:text-2xl font-heading">What brings you here?</CardTitle>
                <CardDescription className="text-sm">Select all that apply</CardDescription>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  {THERAPY_GOALS.map((goal) => (
                    <button
                      key={goal.id}
                      onClick={() => handleGoalToggle(goal.id)}
                      className={`flex items-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl sm:rounded-2xl text-left transition-all duration-300 ${
                        data.therapy_goals.includes(goal.id)
                          ? 'bg-primary/10 ring-2 ring-primary'
                          : 'bg-muted/50 hover:bg-muted'
                      }`}
                      data-testid={`goal-${goal.id}`}
                    >
                      <div className={`p-1.5 sm:p-2 rounded-lg sm:rounded-xl ${
                        data.therapy_goals.includes(goal.id) ? 'bg-primary/20' : 'bg-background'
                      }`}>
                        <goal.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${
                          data.therapy_goals.includes(goal.id) ? 'text-primary' : 'text-muted-foreground'
                        }`} />
                      </div>
                      <span className="text-xs sm:text-sm font-medium">{goal.label}</span>
                      {data.therapy_goals.includes(goal.id) && (
                        <Check className="w-4 h-4 text-primary ml-auto" />
                      )}
                    </button>
                  ))}
                </div>
              </CardContent>
            </>
          )}

          {/* Step 3: Tone */}
          {step === 3 && (
            <>
              <CardHeader className="text-center px-4 sm:px-6">
                <CardTitle className="text-xl sm:text-2xl font-heading">Choose your conversation style</CardTitle>
                <CardDescription className="text-sm">How would you like me to communicate?</CardDescription>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                <div className="space-y-2 sm:space-y-3">
                  {TONE_PREFERENCES.map((tone) => (
                    <button
                      key={tone.id}
                      onClick={() => setData({ ...data, tone_preference: tone.id })}
                      className={`w-full flex items-center gap-3 sm:gap-4 p-4 sm:p-5 rounded-xl sm:rounded-2xl text-left transition-all duration-300 ${
                        data.tone_preference === tone.id
                          ? 'bg-primary/10 ring-2 ring-primary'
                          : 'bg-muted/50 hover:bg-muted'
                      }`}
                      data-testid={`tone-${tone.id}`}
                    >
                      <span className="text-2xl sm:text-3xl">{tone.icon}</span>
                      <div className="flex-1">
                        <div className="font-semibold text-sm sm:text-base">{tone.label}</div>
                        <div className="text-xs sm:text-sm text-muted-foreground">{tone.description}</div>
                      </div>
                      {data.tone_preference === tone.id && (
                        <Check className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                      )}
                    </button>
                  ))}
                </div>
              </CardContent>
            </>
          )}

          {/* Step 4: Disclaimer */}
          {step === 4 && (
            <>
              <CardHeader className="text-center px-4 sm:px-6">
                <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center mb-3 sm:mb-4">
                  <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                </div>
                <CardTitle className="text-xl sm:text-2xl font-heading">Almost there!</CardTitle>
                <CardDescription className="text-sm">Please read and accept before continuing</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
                <div className="p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-muted/50 space-y-3 sm:space-y-4">
                  <h4 className="font-semibold text-foreground text-sm sm:text-base">Important Disclaimer</h4>
                  <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      TPOCARE AI provides emotional support, NOT medical advice or therapy.
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      I am an AI companion, not a licensed mental health professional.
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      If you're in crisis, please contact emergency services or a crisis hotline.
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary">•</span>
                      Your conversations are private and stored securely.
                    </li>
                  </ul>
                </div>

                <div className="flex items-start gap-3 p-3 sm:p-4 rounded-xl bg-card border border-border">
                  <Checkbox
                    id="disclaimer"
                    checked={data.disclaimer_accepted}
                    onCheckedChange={(checked) => setData({ ...data, disclaimer_accepted: checked })}
                    data-testid="disclaimer-checkbox"
                  />
                  <label htmlFor="disclaimer" className="text-xs sm:text-sm leading-relaxed cursor-pointer">
                    I understand that TPOCARE AI provides emotional support only and is not a 
                    substitute for professional mental health care.
                  </label>
                </div>
              </CardContent>
            </>
          )}

          {/* Navigation */}
          <div className="p-4 sm:p-6 pt-0 flex gap-2 sm:gap-3">
            {step > 1 && (
              <Button
                variant="outline"
                onClick={handleBack}
                className="rounded-full text-sm"
                data-testid="back-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            
            {step < totalSteps ? (
              <Button
                onClick={handleNext}
                disabled={!canProceed()}
                className="flex-1 rounded-full btn-hover text-sm"
                data-testid="next-btn"
              >
                Continue
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleComplete}
                disabled={!canProceed() || isLoading}
                className="flex-1 rounded-full btn-hover text-sm"
                data-testid="complete-btn"
              >
                {isLoading ? 'Starting...' : 'Start Chatting'}
                <Sparkles className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Onboarding;
